// src/features/search/searchThunks.ts

import { createAsyncThunk } from '@reduxjs/toolkit';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@/services/firebase/config';
import { SearchResults, Site, InstitutionalDocument } from '@/types/search.types';
import { NewsArticle, EventArticle } from '@/types/news.types';
import { Form } from '@/types/form.types';
import { getColombianStartOfToday } from '@/utils/colombianTimezone';

// Predefined sites (since they're hardcoded in the app)
const sites: Site[] = [
  {
    id: '1',
    name: 'Sede Principal',
    address: 'Calle 5 # 36-00, Popayán, Cauca',
    description: 'Sede principal de la institución',
    studentCount: 500,
  },
  {
    id: '2',
    name: 'Sede de San José',
    address: 'Carrera 32 # 5-82, Popayán, Cauca',
    description: 'Sede San José',
    studentCount: 300,
  },
  {
    id: '3',
    name: 'Sede Chuni',
    address: 'Calle 5 con carrera 37, Popayán, Cauca',
    description: 'Sede Chuni',
    studentCount: 250,
  },
  {
    id: '4',
    name: 'Sede los Campos',
    address: 'Calle 12 # 36a-03, Popayán, Cauca',
    description: 'Sede Los Campos',
    studentCount: 200,
  },
  {
    id: '5',
    name: 'Sede Las Palmas',
    address: 'Calle 6 # 42-06, Popayán, Cauca',
    description: 'Sede Las Palmas',
    studentCount: 150,
  },
];

/**
 * Search news articles
 */
const searchNews = async (searchQuery: string): Promise<NewsArticle[]> => {
  try {
    const newsRef = collection(db, 'news');
    const q = query(newsRef, orderBy('date', 'desc'));
    const snapshot = await getDocs(q);

    const allNews = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as NewsArticle[];

    const lowerQuery = searchQuery.toLowerCase();

    // If searching for "noticias" keyword, return all news
    if (lowerQuery === 'noticias' || lowerQuery === 'noticia') {
      return allNews;
    }

    // Client-side filtering for title, content, and summary
    return allNews.filter(
      (article) =>
        article.title.toLowerCase().includes(lowerQuery) ||
        article.content?.toLowerCase().includes(lowerQuery) ||
        article.summary?.toLowerCase().includes(lowerQuery) ||
        article.tag?.toLowerCase().includes(lowerQuery)
    );
  } catch (error) {
    console.error('Error searching news:', error);
    return [];
  }
};

/**
 * Search events
 */
const searchEvents = async (searchQuery: string): Promise<EventArticle[]> => {
  try {
    const eventsRef = collection(db, 'events');
    const q = query(
      eventsRef,
      where('date', '>=', getColombianStartOfToday()),
      orderBy('date', 'asc')
    );
    const snapshot = await getDocs(q);

    const allEvents = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as EventArticle[];

    const lowerQuery = searchQuery.toLowerCase();

    // If searching for "eventos" keyword, return all events
    if (lowerQuery === 'eventos' || lowerQuery === 'evento') {
      return allEvents;
    }

    // Client-side filtering for title, place, and manager
    return allEvents.filter(
      (event) =>
        event.title.toLowerCase().includes(lowerQuery) ||
        event.place?.toLowerCase().includes(lowerQuery) ||
        event.manager?.toLowerCase().includes(lowerQuery)
    );
  } catch (error) {
    console.error('Error searching events:', error);
    return [];
  }
};

/**
 * Search institutional documents from Firestore
 */
const searchInstitutional = async (searchQuery: string): Promise<InstitutionalDocument[]> => {
  try {
    const docsRef = collection(db, 'idocuments');
    const snapshot = await getDocs(docsRef);

    const allDocs = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.name || data.title || '',
        description: data.description || '',
        url: data.url || data.path || data.docName || '',
        category: data.tag || data.category || '',
      };
    }) as InstitutionalDocument[];

    const lowerQuery = searchQuery.toLowerCase();

    // If searching for "institucional" or "documentos" keywords, return all documents
    if (lowerQuery === 'institucional' || lowerQuery === 'documentos' || lowerQuery === 'documento') {
      return allDocs;
    }

    // Client-side filtering for title, description, and category
    return allDocs.filter(
      (doc) =>
        doc.title.toLowerCase().includes(lowerQuery) ||
        doc.description?.toLowerCase().includes(lowerQuery) ||
        doc.category?.toLowerCase().includes(lowerQuery)
    );
  } catch (error) {
    console.error('Error searching institutional documents:', error);
    return [];
  }
};

/**
 * Search sites (sedes)
 */
const searchSites = (searchQuery: string): Site[] => {
  const lowerQuery = searchQuery.toLowerCase();

  // If searching for "sedes" keyword, return all sites
  if (lowerQuery === 'sedes' || lowerQuery === 'sede') {
    return sites;
  }

  return sites.filter(
    (site) =>
      site.name.toLowerCase().includes(lowerQuery) ||
      site.address.toLowerCase().includes(lowerQuery) ||
      site.description?.toLowerCase().includes(lowerQuery)
  );
};

/**
 * Search forms from Firestore
 */
const searchForms = async (searchQuery: string): Promise<Form[]> => {
  try {
    const formsRef = collection(db, 'forms');
    const snapshot = await getDocs(formsRef);

    const allForms = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Form[];

    const lowerQuery = searchQuery.toLowerCase();

    // If searching for "formularios" keyword, return all forms
    if (lowerQuery === 'formularios' || lowerQuery === 'formulario') {
      return allForms;
    }

    // Client-side filtering for name and type
    return allForms.filter(
      (form) =>
        form.name.toLowerCase().includes(lowerQuery) ||
        form.type?.toLowerCase().includes(lowerQuery)
    );
  } catch (error) {
    console.error('Error searching forms:', error);
    return [];
  }
};

/**
 * Perform comprehensive search across all content types
 */
export const performSearch = createAsyncThunk<
  SearchResults,
  { searchQuery: string; userRole?: string }
>(
  'search/performSearch',
  async ({ searchQuery, userRole }) => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      return {
        news: [],
        events: [],
        institutional: [],
        sites: [],
        forms: [],
      };
    }

    const trimmedQuery = searchQuery.trim();

    // Check if user has permission to access documents (institutional documents)
    const canAccessDocs = userRole && ['admin', 'educator', 'superadmin'].includes(userRole.toLowerCase());

    // Execute all searches in parallel
    const [newsResults, eventsResults, institutionalResults, sitesResults, formsResults] = await Promise.all([
      searchNews(trimmedQuery),
      searchEvents(trimmedQuery),
      canAccessDocs ? searchInstitutional(trimmedQuery) : Promise.resolve([]),
      Promise.resolve(searchSites(trimmedQuery)),
      searchForms(trimmedQuery),
    ]);

    return {
      news: newsResults,
      events: eventsResults,
      institutional: institutionalResults,
      sites: sitesResults,
      forms: formsResults,
    };
  }
);
